import { isEmpty } from '@core/Utils'
import { Action, NoticeCategory, NoticeType, OperationState, TransmissionState } from './IMessage'
import type { ActionNoticeData, AsyncOperationEventData, TransmissionNoticeData } from './DataSyncManager'
import { TransmissionTaskType } from '../transmission/ITask'

const TransmissionTaskTypeList = Object.values(TransmissionTaskType)

interface BaseNoticeBuildData {
  id: string
  timestamp: number
  appId: string
}

abstract class BaseNoticeDataBuilder<T extends BaseNoticeBuildData> {
  protected _timestamp: number | undefined
  protected _appId: string | undefined
  protected _id: string | undefined

  #setAppId(appId: string) {
    if (!isEmpty(appId)) {
      this._appId = appId
    }

    return this
  }

  #setTimestamp(timestamp: number) {
    this._timestamp = timestamp

    return this
  }

  #setId(id: string) {
    if (!isEmpty(id)) {
      this._id = id
    }

    return this
  }

  buildUpon(noticeData: T) {
    this.#setAppId(noticeData.appId).#setId(noticeData.id).#setTimestamp(noticeData.timestamp)

    return this
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fromJson(noticeData: any) {
    typeof noticeData?.appId === 'string' && this.#setAppId(noticeData.appId)
    typeof noticeData?.id === 'string' && this.#setId(noticeData.id)
    typeof noticeData?.timestamp === 'number' && this.#setTimestamp(noticeData.timestamp)

    return this
  }

  protected _isValid(): boolean {
    return this._timestamp !== undefined && this._appId !== undefined && this._id !== undefined
  }

  protected abstract _normalize(temp: T): T

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected _buildImpl(): any | undefined {
    return this._isValid()
      ? {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          appId: this._appId!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          id: this._id!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          timestamp: this._timestamp!,
        }
      : undefined
  }

  build(): T | undefined {
    const res = this._buildImpl()

    return res ? this._normalize(res) : undefined
  }
}

export class ActionNoticeDataBuilder extends BaseNoticeDataBuilder<ActionNoticeData> {
  #category: NoticeCategory = NoticeCategory.NOTICE
  #pagePath?: string | undefined
  #extraQuery?: string | undefined
  #action: Action | undefined
  #actionBtnLabel?: string | undefined
  #msg: string | undefined
  #type: NoticeType | undefined
  #isEmphasized?: boolean

  #setMsg(msg: string | undefined) {
    if (!isEmpty(msg)) {
      this.#msg = msg
    }

    return this
  }

  #setType(type: NoticeType) {
    this.#type = type

    return this
  }

  #setAction(action: Action) {
    this.#action = action

    return this
  }

  #setPagePath(pagePath?: string) {
    if (!isEmpty(pagePath)) {
      this.#pagePath = pagePath
    }

    return this
  }

  #setExtraQuery(extraQuery?: string) {
    if (!isEmpty(extraQuery)) {
      this.#extraQuery = extraQuery
    }

    return this
  }

  #setActionBtnLabel(actionBtnLabel?: string) {
    if (!isEmpty(actionBtnLabel)) {
      this.#actionBtnLabel = actionBtnLabel
    }

    return this
  }

  #setIsEmphasized(isEmphasized?: boolean) {
    this.#isEmphasized = isEmphasized

    return this
  }

  #isValid(): boolean {
    return super._isValid() && this.#action !== undefined && this.#msg !== undefined && this.#type !== undefined
  }

  buildUpon(noticeData: ActionNoticeData) {
    const { msg, type, action, actionBtnLabel, extraQuery, pagePath, isEmphasized } = noticeData

    super
      .buildUpon(noticeData)
      .#setMsg(msg)
      .#setType(type)
      .#setAction(action)
      .#setActionBtnLabel(actionBtnLabel)
      .#setExtraQuery(extraQuery)
      .#setPagePath(pagePath)
      .#setIsEmphasized(isEmphasized)

    return this
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fromJson(noticeData: any) {
    super.fromJson(noticeData)
    typeof noticeData?.msg === 'string' && this.#setMsg(noticeData.msg)
    noticeData?.action in Action && this.#setAction(noticeData.action)
    noticeData?.type in NoticeType && this.#setType(noticeData.type)
    typeof noticeData?.actionBtnLabel === 'string' && this.#setActionBtnLabel(noticeData.actionBtnLabel)
    typeof noticeData?.extraQuery === 'string' && this.#setExtraQuery(noticeData.extraQuery)
    typeof noticeData?.pagePath === 'string' && this.#setPagePath(noticeData.pagePath)
    typeof noticeData?.isEmphasized === 'boolean' && this.#setIsEmphasized(noticeData.isEmphasized)

    return this
  }

  protected _normalize(temp: ActionNoticeData): ActionNoticeData {
    temp.pagePath === undefined && delete temp.pagePath
    temp.extraQuery === undefined && delete temp.extraQuery
    temp.actionBtnLabel === undefined && delete temp.actionBtnLabel
    temp.isEmphasized === undefined && delete temp.isEmphasized

    return temp
  }

  protected _buildImpl(): ActionNoticeData | undefined {
    const baseBuildData = super._buildImpl()

    return baseBuildData && this.#isValid()
      ? {
          category: this.#category,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          action: this.#action!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          msg: this.#msg!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          type: this.#type!,
          pagePath: this.#pagePath,
          extraQuery: this.#extraQuery,
          actionBtnLabel: this.#actionBtnLabel,
          isEmphasized: this.#isEmphasized,
          ...baseBuildData,
        }
      : undefined
  }
}

export class TransmissionNoticeDataBuilder extends BaseNoticeDataBuilder<TransmissionNoticeData> {
  #category: NoticeCategory = NoticeCategory.TRANSMISSION
  #isUpload: boolean | undefined
  #subMsg?: string | undefined
  #targetDevice: string | undefined
  #targetPath?: string | undefined
  #cloudPath?: string | undefined
  #fileName: string | undefined
  #fileSize: number | undefined
  #transmitted: number | undefined
  #state: TransmissionState | undefined
  #webClientId: string | undefined
  #reason: string | undefined
  #targetZipSize: number | undefined
  #businessType: TransmissionTaskType | undefined

  #setIsUpload(isUpload: boolean) {
    this.#isUpload = isUpload

    return this
  }

  #setSubMsg(subMsg?: string) {
    if (!isEmpty(subMsg)) {
      this.#subMsg = subMsg
    }

    return this
  }

  #setTargetDevice(targetDevice: string) {
    if (!isEmpty(targetDevice)) {
      this.#targetDevice = targetDevice
    }

    return this
  }

  #setFileName(fileName: string) {
    if (!isEmpty(fileName)) {
      this.#fileName = fileName
    }

    return this
  }

  #setWebClientId(webClientId: string) {
    if (!isEmpty(webClientId)) {
      this.#webClientId = webClientId
    }

    return this
  }

  #setFileSize(fileSize: number) {
    this.#fileSize = fileSize

    return this
  }

  #setTransmitted(transmitted: number) {
    this.#transmitted = transmitted

    return this
  }

  #setTargetPath(targetPath?: string) {
    if (!isEmpty(targetPath)) {
      this.#targetPath = targetPath
    }

    return this
  }

  #setCloudPath(cloudPath?: string) {
    if (!isEmpty(cloudPath)) {
      this.#cloudPath = cloudPath
    }

    return this
  }

  #setState(state: TransmissionState) {
    this.#state = state

    return this
  }

  #setReason(reason?: string) {
    if (!isEmpty(reason)) {
      this.#reason = reason
    }

    return this
  }

  #setTargetZipSize(targetZipSize?: number) {
    if (targetZipSize !== undefined) {
      this.#targetZipSize = targetZipSize
    }

    return this
  }

  #setBusinessType(businessType?: TransmissionTaskType) {
    this.#businessType = businessType

    return this
  }

  #isValid(): boolean {
    return (
      super._isValid() &&
      this.#isUpload !== undefined &&
      this.#targetDevice !== undefined &&
      this.#fileName !== undefined &&
      this.#fileSize !== undefined &&
      this.#transmitted !== undefined &&
      this.#state !== undefined &&
      this.#webClientId !== undefined
    )
  }

  buildUpon(noticeData: TransmissionNoticeData) {
    const {
      isUpload,
      subMsg,
      targetDevice,
      targetPath,
      fileName,
      fileSize,
      transmitted,
      state,
      webClientId,
      cloudPath,
      reason,
      targetZipSize,
      businessType,
    } = noticeData

    super
      .buildUpon(noticeData)
      .#setFileName(fileName)
      .#setFileSize(fileSize)
      .#setIsUpload(isUpload)
      .#setState(state)
      .#setSubMsg(subMsg)
      .#setTargetDevice(targetDevice)
      .#setTargetPath(targetPath)
      .#setTransmitted(transmitted)
      .#setWebClientId(webClientId)
      .#setCloudPath(cloudPath)
      .#setReason(reason)
      .#setTargetZipSize(targetZipSize)
      .#setBusinessType(businessType)

    return this
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fromJson(noticeData: any) {
    if (noticeData?.businessType === TransmissionTaskType.ZIP) {
      console.log(`[Transmission] zip task ${new Date().toLocaleString()} 收到一条压缩消息：`, noticeData)
      console.log(`[Transmission] zip task ${new Date().toLocaleString()} 压缩进度:`, noticeData?.transmitted)
      console.log(`[Transmission] zip task ${new Date().toLocaleString()} 总大小:`, noticeData?.fileSize)
    }

    super.fromJson(noticeData)
    typeof noticeData?.fileName === 'string' && this.#setFileName(noticeData.fileName)
    typeof noticeData?.fileSize === 'number' && this.#setFileSize(noticeData.fileSize)
    typeof noticeData?.isUpload === 'boolean' && this.#setIsUpload(noticeData.isUpload)
    noticeData?.state in TransmissionState && this.#setState(noticeData.state)
    typeof noticeData?.subMsg === 'string' && this.#setSubMsg(noticeData.subMsg)
    typeof noticeData?.targetDevice === 'string' && this.#setTargetDevice(noticeData.targetDevice)
    typeof noticeData?.targetPath === 'string' && this.#setTargetPath(noticeData.targetPath)
    typeof noticeData?.transmitted === 'number' && this.#setTransmitted(noticeData.transmitted)
    typeof noticeData?.webClientId === 'string' && this.#setWebClientId(noticeData.webClientId)
    typeof noticeData?.cloudPath === 'string' && this.#setCloudPath(noticeData.cloudPath)
    typeof noticeData?.reason === 'string' && this.#setReason(noticeData.reason)
    typeof noticeData?.targetZipSize === 'number' && this.#setTargetZipSize(noticeData.targetZipSize)

    TransmissionTaskTypeList.includes(noticeData?.businessType) && this.#setBusinessType(noticeData.businessType)

    return this
  }

  protected _normalize(temp: TransmissionNoticeData): TransmissionNoticeData {
    temp.subMsg === undefined && delete temp.subMsg
    temp.targetPath === undefined && delete temp.targetPath
    temp.cloudPath === undefined && delete temp.cloudPath
    temp.reason === undefined && delete temp.reason
    temp.targetZipSize === undefined && delete temp.targetZipSize
    temp.businessType === undefined && delete temp.businessType

    return temp
  }

  protected _buildImpl(): TransmissionNoticeData | undefined {
    const baseNoticeData = super._buildImpl()

    return baseNoticeData && this.#isValid()
      ? {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          isUpload: this.#isUpload!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          targetDevice: this.#targetDevice!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          fileName: this.#fileName!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          fileSize: this.#fileSize!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          transmitted: this.#transmitted!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          state: this.#state!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          webClientId: this.#webClientId!,
          category: this.#category,
          subMsg: this.#subMsg,
          targetPath: this.#targetPath,
          cloudPath: this.#cloudPath,
          reason: this.#reason,
          targetZipSize: this.#targetZipSize,
          businessType: this.#businessType,
          ...baseNoticeData,
        }
      : undefined
  }
}

export class AsyncOperationEventDataBuilder extends BaseNoticeDataBuilder<AsyncOperationEventData> {
  #category: NoticeCategory = NoticeCategory.EVENT
  #operation: string | undefined
  #source?: string | undefined
  #target: string | undefined
  #state: OperationState | undefined

  #setOperation(operation: string) {
    if (!isEmpty(operation)) {
      this.#operation = operation
    }

    return this
  }

  #setSource(source?: string) {
    if (!isEmpty(source)) {
      this.#source = source
    }

    return this
  }

  #setTarget(target: string) {
    if (!isEmpty(target)) {
      this.#target = target
    }

    return this
  }

  #setState(state: OperationState) {
    this.#state = state

    return this
  }

  #isValid(): boolean {
    return super._isValid() && this.#operation !== undefined && this.#target !== undefined && this.#state !== undefined
  }

  buildUpon(noticeData: AsyncOperationEventData) {
    const { operation, source, target, state } = noticeData

    super.buildUpon(noticeData).#setOperation(operation).#setSource(source).#setState(state).#setTarget(target)

    return this
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fromJson(noticeData: any) {
    super.fromJson(noticeData)

    typeof noticeData?.operation === 'string' && this.#setOperation(noticeData.operation)
    typeof noticeData?.source === 'string' && this.#setSource(noticeData.source)
    noticeData?.state in OperationState && this.#setState(noticeData.state)
    typeof noticeData?.target === 'string' && this.#setTarget(noticeData.target)

    return this
  }

  protected _normalize(temp: AsyncOperationEventData): AsyncOperationEventData {
    temp.source === undefined && delete temp.source

    return temp
  }

  protected _buildImpl(): AsyncOperationEventData | undefined {
    const baseNoticeData = super._buildImpl()

    return baseNoticeData && this.#isValid()
      ? {
          category: this.#category,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          operation: this.#operation!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          target: this.#target!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          state: this.#state!,
          source: this.#source,
          ...baseNoticeData,
        }
      : undefined
  }
}

export const NoticeFactory = {
  getBuilder: (category: NoticeCategory) => {
    switch (category) {
      case NoticeCategory.NOTICE:
        return new ActionNoticeDataBuilder()
      case NoticeCategory.TRANSMISSION:
        return new TransmissionNoticeDataBuilder()
      case NoticeCategory.EVENT:
        return new AsyncOperationEventDataBuilder()
      default:
        break
    }
  },
}
